import {
  ERegionCode,
  TCoverageSideEffectsType,
  TCoverageUpdateType,
  TOnChangeMockEvent,
  TVehicleCoverageKey,
} from '../../interfaces';
import { REGION_COVERAGE_RULES } from '../constants/regionConfigs';

export type TVehicleCoverageFormValues = {
  // required
  collision_deductible: string;
  new_car_replacement: string;
  oem_parts: string;
  towing_road_services: string;
} & Partial<{
  comprehensive_deductible: never;
  gap: string;
  glass: string;
  loan_lease_payoff: string;
  loss_of_use: string;
  ridesharing: string;
  side_hustle: string;
  temporary_ride: string;
  uninsured_motorist_property_damage_limit: string;
}>;

export type VehicleCoverageOnChangeWrapperProps = {
  onChange?: (event: TOnChangeMockEvent) => void;
  formikValues: TVehicleCoverageFormValues;
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean
  ) => void;
  bodilyInjuryCoverageValue: number;
  callback: ({
    updateType,
    undo,
    sideEffectContext,
  }: {
    updateType: TCoverageUpdateType;
    undo?: () => void;
    sideEffectContext?: TCoverageSideEffectsType;
  }) => void;
  vehicleIndex?: number;
  regionCode?: ERegionCode;
};

const { VUMPD_REQUIRES_NO_COMP_COLL } = REGION_COVERAGE_RULES;

export function vehicleCoverageOnChangeWrapper({
  onChange,
  formikValues,
  setFieldValue,
  bodilyInjuryCoverageValue,
  callback,
  vehicleIndex = 0,
  regionCode = 'SC',
}: VehicleCoverageOnChangeWrapperProps) {
  return function (event: TOnChangeMockEvent) {
    onChange?.(event);

    const { target } = event;
    const { name, value } = target;

    const valueCoverageKeys: Exclude<
      TVehicleCoverageKey,
      'comprehensive_deductible'
    >[] = [
      'oem_parts',
      'new_car_replacement',
      'towing_road_services',
      'loan_lease_payoff',
      'loss_of_use',
      'gap',
      'temporary_ride',
      'side_hustle',
      'uninsured_motorist_property_damage_limit',
      'glass',
    ];
    const isOneOrMoreCollisionDeductibleDependentFieldsIncluded =
      valueCoverageKeys.filter((vehicleCoverageKey) => {
        const value = formikValues?.[vehicleCoverageKey];
        return (
          vehicleCoverageKey !== 'uninsured_motorist_property_damage_limit' &&
          value !== 'null' &&
          value !== 'false' &&
          !!value
        );
      }).length > 0;

    const currentCollisionDeductible = formikValues?.collision_deductible;
    const towingRoadServicesValue = formikValues?.towing_road_services;
    const lossOfUseValue =
      formikValues?.loss_of_use || formikValues?.temporary_ride;

    const isCollisionDeductibleNotIncludedOrSelectOne =
      currentCollisionDeductible === '' ||
      currentCollisionDeductible === 'null';

    const isTowingRoadServicesNotIncludedOrSelectOne =
      towingRoadServicesValue === '' ||
      towingRoadServicesValue === 'null' ||
      towingRoadServicesValue === 'false';

    const isLossOfUseNotIncludedOrSelectOne =
      lossOfUseValue === '' || lossOfUseValue === 'null';

    function undo() {
      Object.entries(formikValues).forEach(([key, value]) => {
        setFieldValue(key, value, true);
      });
    }

    switch (name) {
      case 'oem_parts':
      case 'new_car_replacement':
      case 'towing_road_services':
      case 'side_hustle':
      case 'loan_lease_payoff':
      case 'loss_of_use':
      case 'gap':
      case 'temporary_ride':
        const isFalsyValue =
          value === 'false' || value === 'null' || value === '';
        if (!isFalsyValue && isCollisionDeductibleNotIncludedOrSelectOne) {
          setFieldValue('collision_deductible', '100', true);
          callback({
            updateType: 'COMPREHENSIVE_COLLISION_COVERAGE_UPDATE',
            undo,
          });
        }
        break;
      case 'collision_deductible':
        if (
          (value === 'null' || value === null) &&
          isOneOrMoreCollisionDeductibleDependentFieldsIncluded
        ) {
          valueCoverageKeys.forEach((vehicleCoverageKey) => {
            if (
              vehicleCoverageKey === 'uninsured_motorist_property_damage_limit'
            ) {
              return;
            }
            setFieldValue(
              vehicleCoverageKey,
              vehicleCoverageKey === 'loss_of_use' ||
                vehicleCoverageKey === 'temporary_ride'
                ? 'null'
                : 'false',
              true
            );
          });
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            undo,
            sideEffectContext: {
              type: `VEHICLE_ADD_ONS_REMOVED`,
              name: 'dependent',
              isIncreased: false,
              isRemoved: true,
              cause: `collision_deductible`,
              causeName: 'Comprehensive & Collision',
              vehicleIndex,
            },
          });
        } else if (
          bodilyInjuryCoverageValue >= 100000 &&
          (isTowingRoadServicesNotIncludedOrSelectOne ||
            isLossOfUseNotIncludedOrSelectOne)
        ) {
          setFieldValue('towing_road_services', 'true');

          if (formikValues.loss_of_use) {
            setFieldValue('loss_of_use', '30');
          }
          if (formikValues.temporary_ride) {
            setFieldValue('temporary_ride', '30');
          }
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            undo,
          });
        } else if (
          VUMPD_REQUIRES_NO_COMP_COLL.includes(regionCode) &&
          (value !== null || value !== 'null') &&
          !!formikValues.uninsured_motorist_property_damage_limit &&
          formikValues.uninsured_motorist_property_damage_limit !== 'null'
        ) {
          setFieldValue('uninsured_motorist_property_damage_limit', 'null');
          callback({
            updateType: 'VEHICLE_LEVEL_COVERAGES_UPDATE',
            sideEffectContext: {
              type: `VEHICLE_UMPD_UPDATE`,
              name: 'Uninsured Motorist Property Damage',
              isIncreased: false,
              isRemoved: true,
              cause: `collision_deductible`,
              causeName: 'Comprehensive & Collision',
              vehicleIndex,
            },
          });
        }
        break;
      default:
        break;
    }
  };
}
